import { useMemo } from "react";
import type { ConsentViewT } from "../../../types/database/consents";
import { useAuth } from "../../../utils/auth";

/**
 * Function to check if a single consent has been signed by the authenticated user
 * @param consent The consent to check
 * @param userId The user ID to check against (optional, defaults to current auth user)
 * @returns Boolean indicating if the consent has been signed by the user
 */
export const hasUserSignedConsent = (
	consent: ConsentViewT,
	userId?: string,
): boolean => {
	const auth = useAuth();
	const targetUserId = userId || auth?.id;

	if (!consent || !targetUserId) {
		return false;
	}

	// Check if user has already signed this consent
	const userSignature = consent.signatures?.find(
		(sig) => sig.user_id === targetUserId && sig.signed_at,
	);

	return Boolean(userSignature);
};

/**
 * Hook to check if the current user has unsigned consents
 * Returns array of unsigned consents that require signature
 */
const useConsent = (consents: ConsentViewT[]) => {
	const auth = useAuth();

	// Use useMemo to memoize the result and only recalculate when consents or auth.id changes
	return useMemo(() => {
		if (!consents || consents.length === 0 || !auth?.id) {
			return [];
		}

		// Filter consents that don't have the current user's signature
		const unsignedConsents = consents.filter((consent) => {
			// Use the hasUserSignedConsent function for consistency
			return !hasUserSignedConsent(consent);
		});

		return unsignedConsents;
	}, [consents, auth?.id]);
};

export default useConsent;
